import {
    doc,
    getDoc,
    setDoc,
    updateDoc,
    serverTimestamp
} from 'firebase/firestore';
import { db } from './config';
import { uploadImageToCloudinary } from '../services/cloudinaryService';

// Profile data interface
export interface ProfileData {
    id: string;
    fullName: string;
    title: string;
    email: string;
    phone: string;
    location: string;
    bio: string;
    shortBio: string;
    photoURL: string;
    skills: {
        id: number;
        category: string;
        items?: string[]; // Making items optional for backward compatibility
        subcategories?: {
            name: string;
            skills: string[];
        }[];
    }[];
    approachItems: {
        id: number;
        title: string;
        description: string;
    }[];
    resumeSummary: string;
    skillsDescription: string;
    projectsDescription: string;
    socialLinks: {
        linkedin: string;
        github: string;
        twitter?: string;
        website?: string;
        googleScholar?: string;
        researchGate?: string;
        orcid?: string;
    };
    // New fields for academic audiences
    researchInterests: {
        id: number;
        title: string;
        description: string;
    }[];
    teachingExperience: {
        id: number;
        title: string;
        description: string;
    }[];
    // New fields for industry recruiters
    softSkills: {
        id: number;
        title: string;
        description: string;
    }[];
    industryKnowledge: string[];
    certifications: {
        id: number;
        name: string;
        issuer: string;
        date: string;
        url?: string;
    }[];
    languages: {
        id: number;
        name: string;
        proficiency: string;
    }[];
    // New fields for both audiences
    personalStatement: string;
    researchSummary: string;
    mentorshipExperience: string;
    lastUpdated: any;
}

// Default profile data
export const defaultProfile: ProfileData = {
    id: 'main-profile',
    fullName: 'Haraprosad Biswas',
    title: 'Flutter Developer',
    email: 'hello@haraprosadbiswas.com',
    phone: '+880 1234 567890',
    location: 'Dhaka, Bangladesh',
    bio: 'Results-driven Flutter developer with 4+ years of experience crafting high-performance, user-centric mobile apps. Specializing in Flutter (3+ years) and native Android (1+ year), I excel in state management, RESTful APIs, Firebase, and Clean Architecture.',
    shortBio: 'Flutter Developer crafting high-performance mobile apps',
    photoURL: 'https://placehold.co/300x300/EEE/31343C',
    skills: [
        {
            id: 1,
            category: 'Mobile Development',
            items: ['Flutter', 'Dart', 'Android', 'State Management', 'Firebase'],
        },
        {
            id: 2,
            category: 'Web Development',
            items: ['React.js', 'HTML/CSS', 'JavaScript', 'Tailwind CSS', 'Shadcn UI'],
        },
        {
            id: 3,
            category: 'Backend',
            items: ['FastAPI', 'PostgreSQL', 'Firebase', 'RESTful APIs'],
        },
        {
            id: 4,
            category: 'Other',
            items: ['ML', 'LangChain', 'LangGraph', 'Git', 'Agile Methodology', 'TDD'],
        },
    ],
    approachItems: [
        {
            id: 1,
            title: 'Clean Architecture',
            description: 'Building maintainable and scalable applications through proper separation of concerns.'
        },
        {
            id: 2,
            title: 'User-Centric Design',
            description: 'Creating intuitive interfaces that prioritize user experience and accessibility.'
        },
        {
            id: 3,
            title: 'Performance Optimization',
            description: 'Ensuring applications run smoothly on all devices with efficient resource usage.'
        },
        {
            id: 4,
            title: 'Continuous Learning',
            description: 'Staying updated with the latest technologies and best practices in mobile development.'
        }
    ],
    resumeSummary: 'Results-driven Flutter developer with 4+ years of experience crafting high-performance, user-centric mobile apps. Specializing in Flutter (3+ years) and native Android (1+ year), I excel in state management, RESTful APIs, Firebase, and Clean Architecture. Key contributions include the Yebook audiobook app (500K+ installs) and architecting IoT-driven Smart Water Electric Meter solutions. Passionate about building scalable, responsive, and globally accessible apps with seamless UX and optimized performance.',
    skillsDescription: 'I\'ve worked with a variety of technologies, focusing primarily on mobile app development with Flutter.',
    projectsDescription: 'Here are some of my recent projects. I\'ve worked on a variety of apps, from audiobooks to IoT solutions.',
    socialLinks: {
        linkedin: 'https://linkedin.com/in/haraprosadbiswas',
        github: 'https://github.com/Haraprosad',
        twitter: '',
        website: '',
        googleScholar: '',
        researchGate: '',
        orcid: '',
    },
    // Default values for new academic fields
    researchInterests: [
        {
            id: 1,
            title: "Machine Learning",
            description: "Exploring novel applications of machine learning algorithms to solve real-world problems."
        },
        {
            id: 2,
            title: "Natural Language Processing",
            description: "Developing and implementing NLP solutions for text analysis and understanding."
        }
    ],
    teachingExperience: [],
    // Default values for new recruiter-focused fields
    softSkills: [
        {
            id: 1,
            title: "Problem Solving",
            description: "Strong analytical thinking and creative problem-solving abilities."
        },
        {
            id: 2,
            title: "Communication",
            description: "Excellent written and verbal communication skills with technical and non-technical stakeholders."
        }
    ],
    industryKnowledge: ["Mobile App Development", "UI/UX Design", "Software Architecture"],
    certifications: [],
    languages: [
        {
            id: 1,
            name: "English",
            proficiency: "Professional"
        },
        {
            id: 2,
            name: "Bengali",
            proficiency: "Native"
        }
    ],
    // New fields for both audiences
    personalStatement: "I am passionate about creating technology that makes a positive impact on people's lives. My background in both research and industry allows me to approach problems with a unique perspective.",
    researchSummary: "My research focuses on applying machine learning techniques to solve practical problems in mobile and web applications.",
    mentorshipExperience: "",
    lastUpdated: null,
};

const PROFILE_DOC_ID = 'main-profile';

// Get profile data
export const getProfile = async (): Promise<ProfileData> => {
    try {
        const docRef = doc(db, 'profile', PROFILE_DOC_ID);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data() as ProfileData;
        } else {
            // If profile doesn't exist, create it with default data
            await setDoc(docRef, {
                ...defaultProfile,
                lastUpdated: serverTimestamp(),
            });
            return defaultProfile;
        }
    } catch (error) {
        console.error('Error getting profile:', error);
        throw error;
    }
};

// Update profile data
export const updateProfile = async (profileData: Partial<ProfileData>): Promise<void> => {
    try {
        const docRef = doc(db, 'profile', PROFILE_DOC_ID);
        await updateDoc(docRef, {
            ...profileData,
            lastUpdated: serverTimestamp(),
        });
    } catch (error) {
        console.error('Error updating profile:', error);
        throw error;
    }
};

// Upload profile image using Cloudinary
export const uploadProfileImage = async (
    file: File,
    progressCallback?: (progress: number) => void
): Promise<string> => {
    try {
        // Use Cloudinary service to upload the file
        const imageUrl = await uploadImageToCloudinary(file, progressCallback);
        return imageUrl;
    } catch (error) {
        console.error('Error uploading image to Cloudinary:', error);
        throw error;
    }
};

// Delete profile image (Note: Client-side Cloudinary deletion is not possible without exposing API credentials)
export const deleteProfileImage = async (imageUrl: string): Promise<void> => {
    // For Cloudinary free tier, we'll simply replace the old image when uploading a new one
    // This is a safe approach for a portfolio site
    console.log('Old image will be replaced with new upload', imageUrl);

    // If deletion is critical in the future, you would need a server-side solution
    // For now, we just return without error as no action is needed client-side
    return Promise.resolve();
};